import { useState, useEffect, createContext, useContext, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from "@supabase/supabase-js";
import { clearCart } from "@/lib/sessionUtils";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
  loading: boolean;
  adminLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [adminLoading, setAdminLoading] = useState(true);

  const checkAdminRole = async (userId: string) => {
    // Use a non-single query to avoid edge cases where duplicate rows (or RLS quirks)
    // can cause maybeSingle() to throw and incorrectly mark an admin as non-admin.
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .eq('role', 'admin')
      .limit(1);

    if (error) {
      console.error('Error checking admin role:', error);
      return false;
    }

    return (data?.length ?? 0) > 0;
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);
        
        // Defer admin check to avoid Supabase client deadlock
        if (session?.user) {
          setAdminLoading(true);
          setTimeout(() => {
            checkAdminRole(session.user.id).then((result) => {
              setIsAdmin(result);
              setAdminLoading(false);
            });
            // Link staff-created orders only for a verified matching email (server-checked).
            if (event === "SIGNED_IN" && session.user.email_confirmed_at) {
              supabase.functions.invoke("link-assisted-customer").catch(() => {});
            }
          }, 0);
        } else {
          setIsAdmin(false);
          setAdminLoading(false);
        }
        
        setLoading(false);
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      // Always set loading to false immediately so the UI is not blocked
      setLoading(false);
      
      if (session?.user) {
        checkAdminRole(session.user.id).then((result) => {
          setIsAdmin(result);
          setAdminLoading(false);
        });
      } else {
        setAdminLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/`;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
      },
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    // Clear cart and session data for guest users to start fresh
    clearCart();
    sessionStorage.removeItem("checkoutFormData");
    sessionStorage.removeItem("savedShippingAddresses");
    sessionStorage.removeItem("defaultAddressId");
    sessionStorage.removeItem("quizData");
    sessionStorage.removeItem("quizCurrentStep");
    sessionStorage.removeItem("quizFormData");
    sessionStorage.removeItem("selectedOption");
    sessionStorage.removeItem("selectedPerfume");
    sessionStorage.removeItem("perfumes");
    sessionStorage.removeItem("greetingMessage");
    sessionStorage.removeItem("photoPreview");
    sessionStorage.removeItem("currentOrderId");
    sessionStorage.removeItem("highestPhaseReached");
    
    await supabase.auth.signOut();
    setIsAdmin(false);
    
    // Trigger storage event for header to update cart count
    window.dispatchEvent(new Event("storage"));
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/auth?recovery=true`;
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    return { error: error as Error | null };
  };

  return (
    <AuthContext.Provider value={{ user, session, isAdmin, loading, adminLoading, signIn, signUp, signOut, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
